import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getPdfjsLib, normalizeDoc, compressFile, compressPdfClientSide } from '../../utils/documents';
import './DocumentViewer.css';

const PREVIEW_W = 165;
const PREVIEW_H = 147;

export function PdfThumbnail({ url }) {
  const canvasRef = useRef(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    // ⚠️ Le document PDF doit être DÉTRUIT une fois la vignette peinte. Sans cela
    // chaque aperçu garde en mémoire tout le PDF analysé (polices, images
    // décompressées) pour la durée de vie du composant — et même après, la
    // fonction de nettoyage ne faisant que lever un drapeau. Mesuré sur la page
    // Garantie ▸ Relevés (65 PDF affichés d'un coup) : 14 → 64 Mo à la première
    // ouverture, 131 Mo après un aller-retour, 178 Mo après deux, sans jamais
    // redescendre — au bout de quelques navigations l'onglet Chrome est tué
    // (« Aïe aïe aïe », code 5). Le canvas, lui, garde ses pixels : détruire le
    // document après le rendu ne coûte rien à l'affichage.
    let tache = null;
    (async () => {
      try {
        const pdfjs = await getPdfjsLib();
        tache = pdfjs.getDocument({ url, withCredentials: false });
        const pdf   = await tache.promise;
        if (cancelled) { pdf.destroy(); return; }
        const page = await pdf.getPage(1);
        const canvas = canvasRef.current;
        if (cancelled || !canvas) { pdf.destroy(); return; }
        const DPR  = window.devicePixelRatio || 1;
        const vp0  = page.getViewport({ scale: 1 });
        const scale = Math.max(PREVIEW_W / vp0.width, PREVIEW_H / vp0.height) * DPR;
        const vp    = page.getViewport({ scale });
        canvas.width  = vp.width;
        canvas.height = vp.height;
        canvas.style.width  = Math.round(vp.width  / DPR) + 'px';
        canvas.style.height = Math.round(vp.height / DPR) + 'px';
        try {
          await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
        } finally {
          page.cleanup();
          pdf.destroy();   // libère aussi le côté worker
          tache = null;
        }
      } catch (e) {
        console.warn('PDF thumbnail:', e);
        if (!cancelled) setError(true);
      }
    })();
    // Démontage en cours de chargement : annuler la tâche, sinon elle continue de
    // télécharger et d'analyser un PDF dont plus personne ne veut.
    return () => { cancelled = true; if (tache) { tache.destroy(); tache = null; } };
  }, [url]);

  if (error) return (
    <div className="doc-icon">
      <span className="doc-icon-emoji">📄</span>
      <span className="doc-icon-label">PDF</span>
    </div>
  );
  return <canvas ref={canvasRef} />;
}

export function DocumentViewer({ doc, onClose }) {
  const mimeType = doc.type || doc.type_mime || '';
  const isImage  = mimeType.startsWith('image/');
  const isPdf    = mimeType === 'application/pdf';

  return (
    <div className="doc-viewer-overlay" onClick={onClose}>
      <div className="doc-viewer-content" onClick={(e) => e.stopPropagation()}>
        <div className="doc-viewer-header">
          <span className="doc-viewer-title">{doc.name}</span>
          <button className="doc-viewer-close" onClick={onClose}>✕</button>
        </div>

        <div className="doc-viewer-body">
          {isImage && (
            <img src={doc.url} alt={doc.name} className="doc-viewer-image" />
          )}
          {isPdf && (
            <embed src={doc.url} type="application/pdf" className="doc-viewer-pdf" />
          )}
          {!isImage && !isPdf && (
            <div className="doc-viewer-fallback">
              <span style={{ fontSize: '60px' }}>📎</span>
              <p>{doc.name}</p>
              <a href={doc.url} download={doc.name} className="btn-primary">
                Télécharger
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function CommentDocThumb({ doc }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const mimeType = doc.type || doc.type_mime || '';
  const isImage = mimeType.startsWith('image/');
  const isPdf   = mimeType === 'application/pdf';
  const url     = doc.url || null;
  return (
    <>
      <div className="doc-thumbnail" title={doc.name || doc.nom}>
        <div className="doc-preview" onClick={() => url && setLightboxOpen(true)}>
          {url && isImage ? (
            <img src={url} alt={doc.name || doc.nom} />
          ) : url && isPdf ? (
            <PdfThumbnail url={url} />
          ) : (
            <div className="doc-icon">
              <span className="doc-icon-emoji">{isPdf ? '📄' : '📎'}</span>
              <span className="doc-icon-label">{isPdf ? 'PDF' : 'DOC'}</span>
            </div>
          )}
        </div>
        <div className="doc-name">{doc.name || doc.nom}</div>
      </div>
      {lightboxOpen && url && (
        <DocumentViewer doc={normalizeDoc(doc)} onClose={() => setLightboxOpen(false)} />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DocumentSection — section « 📎 Documents » réutilisable (aperçu + upload + suppression).
// Paramétré par une API docs (docsApi) et l'id parent (parentId), pour être partagé
// entre modules (Litiges, Support & Innovation…). Format identique partout.
//   docsApi : { uploadFile(parentId, formData), presign(parentId,{filename,contentType}),
//               confirm(parentId,{key,filename,contentType,size}), rename(parentId,docId,nom),
//               delete(parentId,docId) }
//   parentId : id de l'entité parente (null = mode local : on conserve les blobs en state,
//              le parent les uploadera après création).
//   canEdit  : false → lecture seule (pas de zone de dépôt ni suppression).
// ─────────────────────────────────────────────────────────────────────────────
export function DocumentSection({ documents, setDocuments, parentId, docsApi, canEdit = true }) {
  const [dragging, setDragging] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [docToDelete, setDocToDelete] = useState(null);
  const fileInputRef = useRef(null);

  // MODE LIVE : upload S3 direct
  const uploadFile = useCallback(async (file) => {
    const tempId = Date.now() + Math.random();
    const tempDoc = {
      id: tempId, name: file.name, type: file.type,
      url: URL.createObjectURL(file), isLocal: true, uploading: true,
    };
    setDocuments(prev => [...prev, tempDoc]);

    try {
      let doc;
      if (file.type === 'application/pdf') {
        const compressed = await compressPdfClientSide(file);
        const formData = new FormData();
        formData.append('file', compressed, compressed.name);
        const res = await docsApi.uploadFile(parentId, formData);
        doc = normalizeDoc(res.data.document);
      } else {
        const compressed = await compressFile(file);
        const presignRes = await docsApi.presign(parentId, {
          filename: compressed.name, contentType: compressed.type,
        });
        await fetch(presignRes.data.uploadUrl, {
          method: 'PUT', body: compressed, headers: { 'Content-Type': compressed.type },
        });
        const confirmRes = await docsApi.confirm(parentId, {
          key: presignRes.data.key, filename: compressed.name,
          contentType: compressed.type, size: compressed.size,
        });
        doc = normalizeDoc(confirmRes.data.document);
      }

      URL.revokeObjectURL(tempDoc.url);
      setDocuments(prev => prev.map(d => d.id === tempId ? { ...doc, isLocal: false } : d));
    } catch (err) {
      console.error('Erreur upload:', err);
      setDocuments(prev => {
        const d = prev.find(x => x.id === tempId);
        if (d?.url?.startsWith('blob:')) URL.revokeObjectURL(d.url);
        return prev.filter(x => x.id !== tempId);
      });
      alert(`Erreur lors de l'upload de "${file.name}"`);
    }
  }, [parentId, docsApi, setDocuments]);

  // MODE LOCAL : ajout en state (blob)
  const addLocalFile = useCallback(async (file) => {
    if (file.type === 'application/pdf') {
      const tempId  = Date.now() + Math.random();
      const tempUrl = URL.createObjectURL(file);
      setDocuments(prev => [...prev, { id: tempId, file, name: file.name, type: file.type, url: tempUrl, isLocal: true }]);
      compressPdfClientSide(file).then(compressed => {
        URL.revokeObjectURL(tempUrl);
        setDocuments(prev => prev.map(d => d.id === tempId ? { ...d, file: compressed, url: URL.createObjectURL(compressed) } : d));
      });
    } else {
      const processed = file.type.startsWith('image/') ? await compressFile(file) : file;
      setDocuments(prev => [...prev, {
        id: Date.now() + Math.random(),
        file: processed, name: processed.name, type: processed.type,
        url: URL.createObjectURL(processed), isLocal: true,
      }]);
    }
  }, [setDocuments]);

  const renameDocument = useCallback(async (doc, newName) => {
    if (!doc.isLocal && parentId && docsApi.rename) {
      try { await docsApi.rename(parentId, doc.id, newName); }
      catch { alert('Erreur lors du renommage'); return; }
    }
    setDocuments(prev => prev.map(d => d.id === doc.id ? { ...d, name: newName } : d));
  }, [parentId, docsApi, setDocuments]);

  const addFiles = useCallback((files) => {
    Array.from(files).forEach(file => { parentId ? uploadFile(file) : addLocalFile(file); });
  }, [parentId, uploadFile, addLocalFile]);

  const removeDocument = useCallback(async (doc) => {
    if (!doc.isLocal && parentId) {
      try { await docsApi.delete(parentId, doc.id); }
      catch { alert('Erreur lors de la suppression du document'); return; }
    }
    if (doc.url?.startsWith('blob:')) URL.revokeObjectURL(doc.url);
    setDocuments(prev => prev.filter(d => d.id !== doc.id));
  }, [parentId, docsApi, setDocuments]);

  const handleDrop = (e) => { e.preventDefault(); setDragging(false); if (canEdit) addFiles(e.dataTransfer.files); };

  const handlePaste = useCallback((e) => {
    if (!canEdit) return;
    const items = Array.from(e.clipboardData?.items || []);
    const imageFiles = items
      .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
      .map(item => item.getAsFile())
      .filter(Boolean);
    if (imageFiles.length === 0) return;
    e.preventDefault();
    const named = imageFiles.map((f, i) => new File([f], `capture-${Date.now()}-${i}.${f.type.split('/')[1] || 'png'}`, { type: f.type }));
    addFiles(named);
  }, [canEdit, addFiles]);

  return (
    <div className="documents-section" onPaste={handlePaste}>
      <h4 className="documents-title">📎 Documents</h4>

      <div className="documents-grid">
        {documents.map(doc => (
          <DocumentThumbnail
            key={doc.id}
            doc={doc}
            canEdit={canEdit}
            onRemove={() => setDocToDelete(doc)}
            onClick={() => !doc.uploading && setLightbox(doc)}
            onRename={(newName) => renameDocument(doc, newName)}
          />
        ))}

        {canEdit && (
          <div
            className={`drop-zone${dragging ? ' dragging' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            title="Glisser-déposer ou cliquer pour ajouter un document"
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.msg"
              style={{ display: 'none' }}
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            />
            <div className="drop-zone-content">
              <span className="drop-icon">📤</span>
              <span>Glisser-déposer<br />ou cliquer</span>
            </div>
          </div>
        )}

        {!canEdit && documents.length === 0 && (
          <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>Aucun document.</p>
        )}
      </div>

      {lightbox && <DocumentViewer doc={lightbox} onClose={() => setLightbox(null)} />}

      {docToDelete && (
        <div className="doc-confirm-overlay" onClick={() => setDocToDelete(null)}>
          <div className="doc-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <p className="doc-confirm-msg">Supprimer le document&nbsp;?</p>
            <p className="doc-confirm-name">« {docToDelete.name} »</p>
            <div className="doc-confirm-actions">
              <button className="btn-secondary" onClick={() => setDocToDelete(null)}>Annuler</button>
              <button className="btn-danger" onClick={() => { removeDocument(docToDelete); setDocToDelete(null); }}>🗑️ Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DocumentThumbnail({ doc, onRemove, onClick, onRename, canEdit = true }) {
  const mimeType = doc.type || doc.type_mime || '';
  const isImage  = mimeType.startsWith('image/');
  const isPdf    = mimeType === 'application/pdf';
  const isMsg    = mimeType === 'application/vnd.ms-outlook' || (doc.name || '').toLowerCase().endsWith('.msg');

  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName]   = useState(doc.name || '');
  const inputRef = useRef(null);

  useEffect(() => { if (!renaming) setNewName(doc.name || ''); }, [doc.name, renaming]);
  useEffect(() => { if (renaming) inputRef.current?.focus(); }, [renaming]);

  const submitRename = () => {
    const trimmed = newName.trim();
    if (trimmed && trimmed !== doc.name) onRename(trimmed);
    setRenaming(false);
  };

  return (
    <div className={`doc-thumbnail${doc.uploading ? ' uploading' : ''}`} title={doc.name}>
      {doc.uploading && (
        <div className="doc-uploading-overlay"><span className="doc-uploading-spinner" /></div>
      )}

      {canEdit && (
        <button className="doc-remove-btn" onClick={(e) => { e.stopPropagation(); onRemove(); }} title="Supprimer ce document" disabled={doc.uploading}>✕</button>
      )}

      <div className="doc-preview" onClick={!doc.uploading ? onClick : undefined}>
        {isImage ? (
          <img src={doc.url} alt={doc.name} />
        ) : isPdf ? (
          <PdfThumbnail url={doc.url} />
        ) : isMsg ? (
          <div className="doc-icon"><span className="doc-icon-emoji">✉️</span><span className="doc-icon-label">EMAIL</span></div>
        ) : (
          <div className="doc-icon"><span className="doc-icon-emoji">📎</span><span className="doc-icon-label">DOC</span></div>
        )}
      </div>

      {renaming ? (
        <input
          ref={inputRef}
          className="doc-rename-input"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onBlur={submitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submitRename();
            if (e.key === 'Escape') { setNewName(doc.name || ''); setRenaming(false); }
          }}
        />
      ) : (
        <div className="doc-name" title={canEdit ? 'Double-cliquer pour renommer' : doc.name} onDoubleClick={() => { if (canEdit && !doc.uploading) setRenaming(true); }}>
          {doc.name}
        </div>
      )}
    </div>
  );
}
